import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../../layouts/DashboardLayout';
import { ProgressStepper } from '../ui/ProgressStepper';
import { useAuth } from '../../context/AuthContext';
import { fetchIdeas, finalSubmitIdea } from '../../api/idea';

interface Idea {
  _id: string;
  title: string;
  problem: string;
  solution: string;
  status: string;
}

export function FinalSubmission() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const { ideaId, hackathonId, finalSubmissionStart, finalSubmissionEnd } =
    (location.state as { ideaId?: string; hackathonId?: string; finalSubmissionStart?: string; finalSubmissionEnd?: string } | null) ?? {};

  const [idea, setIdea] = useState<Idea | null>(null);
  const [problem, setProblem] = useState('');
  const [solution, setSolution] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Check if current time is within final submission period
  const now = new Date();
  const submissionAllowed = true; // Allow submission regardless of deadline
  
  const deadlinePassed = finalSubmissionEnd
    ? (() => {
        const end = new Date(finalSubmissionEnd);
        end.setHours(23, 59, 59, 999);
        return now > end;
      })()
    : false;

  const notYetOpen = finalSubmissionStart
    ? now < new Date(finalSubmissionStart)
    : false;

  useEffect(() => {
    if (!token) return;
    fetchIdeas(token, { hackathonId: hackathonId || undefined })
      .then((data) => {
        const found: Idea | undefined = ideaId
          ? data.items?.find((i: Idea) => i._id === ideaId)
          : data.items?.find((i: Idea) =>
              i.status === 'under_mentor_review' || i.status === 'submitted_final' || i.status === 'selected'
            );
        if (found) {
          setIdea(found);
          setProblem(found.problem);
          setSolution(found.solution);
        } else {
          setError('No idea eligible for final submission was found.');
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token, ideaId, hackathonId]);

  const deadlineLabel = finalSubmissionStart && finalSubmissionEnd
    ? `${new Date(finalSubmissionStart).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      })} - ${new Date(finalSubmissionEnd).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      })}`
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !idea) return;
    setSubmitting(true);
    setError(null);
    try {
      await finalSubmitIdea(token, idea._id, { problem, solution, attachments });
      // Fetch updated idea to get attachment URLs
      const updated = await fetchIdeas(token, { hackathonId: hackathonId || undefined });
      const updatedIdea = updated.items?.find((i: Idea) => i._id === idea._id);
      if (updatedIdea) {
        setUploadedAttachments(updatedIdea.attachments || []);
      }
      setSubmitted(true);
      toast.success('Final submission successful!');
    } catch (err: any) {
      setError(err.message ?? 'Final submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500">Loading your idea...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto">
          <button
            onClick={() => submitted ? navigate('/user-dashboard/idea-list') : navigate('/user-dashboard/idea-list')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors mb-6"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Idea List
          </button>

          <div className="mb-8">
            <h1 className="text-4xl text-gray-900 mb-2">Final Submission</h1>
            <p className="text-gray-500">
              Update your idea based on mentor feedback and finalize your submission
            </p>
          </div>

          <div className="space-y-6">
            <ProgressStepper />

            {submitted ? (
              <div className="bg-white rounded-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Submission Successful</h2>
                    <p className="text-gray-600">Your idea has been submitted for final consideration.</p>
                  </div>
                </div>

                {uploadedAttachments.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Documents</h3>
                    <div className="space-y-2 border-t pt-4">
                      {uploadedAttachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-3">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <p className="font-medium text-gray-900">{attachment.filename || `Document ${index + 1}`}</p>
                              <p className="text-xs text-gray-500">{attachment.type || 'PDF/PPT'}</p>
                            </div>
                          </div>
                          {attachment.url && (
                            <button
                              onClick={() => window.open(attachment.url, '_blank')}
                              className="text-xs px-3 py-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors font-medium"
                            >
                              Preview
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-6">
                  <button
                    onClick={() => navigate('/user-dashboard/idea-list')}
                    className="flex-1 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Return to Ideas
                  </button>
                </div>
              </div>
            ) : (
              <>
                {deadlineLabel && (
              <div className={`border rounded-xl p-4 flex gap-3 ${
                deadlinePassed
                  ? 'bg-gray-100 border-gray-300'
                  : notYetOpen
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-green-50 border-green-200'
              }`}>
                <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  deadlinePassed ? 'text-gray-500' : notYetOpen ? 'text-yellow-600' : 'text-green-600'
                }`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">Final Submission Period</p>
                  <p className="text-sm text-gray-600">
                    {deadlinePassed
                      ? <>Deadline was <span className="font-semibold">{deadlineLabel}</span>. Submissions are closed.</>
                      : notYetOpen
                      ? <>Submissions open from <span className="font-semibold">{deadlineLabel}</span>.</>
                      : <>You have until <span className="font-semibold">{new Date(finalSubmissionEnd!).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span> to submit.</>}
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {idea && !submitted && (
              <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 space-y-6">
                <div>
                  <label className="block text-sm mb-2 text-gray-900">Idea Title</label>
                  <input
                    type="text"
                    value={idea.title}
                    readOnly
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-900">
                    Idea Statement <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={problem}
                    onChange={(e) => setProblem(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-900">
                    Updated Solution <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={solution}
                    onChange={(e) => setSolution(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-900">
                    Updated Attachments (PDF/PPT)
                  </label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.ppt,.pptx"
                    onChange={(e) => setAttachments(Array.from(e.target.files ?? []))}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {attachments.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {attachments.map((f, i) => (
                        <li key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg">
                          <span>{f.name}</span>
                          <button
                            type="button"
                            onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                            className="text-red-400 hover:text-red-600 ml-2"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-8 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || idea?.status === 'selected'}
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Uploading...' : idea?.status === 'selected' ? 'Selected by Jury' : 'Upload'}
                  </button>
                </div>
              </form>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
